import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./arap.css";
import { Check, X, RefreshCcw } from "lucide-react";

export default function ApprovalsQueue() {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [me, setMe] = useState({ userId: null, role: "is_member" });

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("PENDING"); // PENDING/APPROVED/REJECTED

  useEffect(() => {
    (async () => {
      if (!companyId) return;

      const { data, error } = await supabase.rpc("rpc_my_company_role", {
        _company_id: companyId,
      });

      if (error) {
        console.error("Role fetch error:", error);
        setMe({ userId: null, role: "is_member", isAdmin: false });
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;

      setMe({
        userId: row?.user_id,
        role: row?.role || "member",
        isAdmin: !!row?.is_admin,
      });
    })();
  }, [companyId]);

  const isAdmin = me.isAdmin;

  async function reload() {
    setErr("");
    if (!companyId) return;
    setBusy(true);
    try {
      const { data, error } = await supabase
        .from("approval_requests")
        .select("*")
        .eq("company_id", companyId)
        .eq("status", status)
        .order("created_at", { ascending: false })
        .limit(200);

      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      setErr(e?.message || "Failed to load approvals queue.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, status]);

  const grouped = useMemo(() => {
    const byType = {};
    for (const r of rows) {
      const key = r.request_type || "UNKNOWN";
      byType[key] = byType[key] || [];
      byType[key].push(r);
    }
    return byType;
  }, [rows]);

  async function decide(reqId, decision) {
    setErr("");
    if (!isAdmin) return setErr("Only admins can approve/reject.");
    setBusy(true);
    try {
      const comment = decision === "APPROVE" ? "Approved" : "Rejected";

      const { error } = await supabase.rpc("rpc_decide_approval", {
        p_request_id: reqId,
        p_decision: decision,
        p_comment: comment,
      });

      if (error) throw error;
      await reload();
    } catch (e) {
      setErr(e?.message || "Failed to decide approval.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="arap-page">
      <header className="arap-head panel">
        <div>
          <div className="arap-title">Approvals Queue</div>
          <div className="arap-sub">
            Member initiates • Admin approves/rejects • Audit captured
          </div>
        </div>

        <div className="arap-actions">
          <select
            className="arap-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={busy}
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>

          <button className="btn-ghost" onClick={reload} disabled={busy}>
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>
      </header>

      {!isAdmin && (
        <div className="banner warn panel">
          You are not an admin. You can view approvals but cannot decide them.
        </div>
      )}

      {err && <div className="banner error panel">{err}</div>}

      {rows.length === 0 ? (
        <section className="panel arap-card">
          <div className="empty-state">
            No {status.toLowerCase()} approval requests found.
          </div>
        </section>
      ) : (
        Object.keys(grouped).map((t) => (
          <section
            key={t}
            className="panel arap-card"
            style={{ marginBottom: 12 }}
          >
            <div className="arap-card-head">
              <h3>{t}</h3>
              <span className="chip">{grouped[t].length}</span>
            </div>

            <div className="arap-table-wrap">
              <table className="arap-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Reason</th>
                    <th className="mono">Entity</th>
                    <th className="mono">Payload</th>
                    <th className="center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[t].map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.created_at).toLocaleString()}</td>
                      <td>{r.reason || "—"}</td>
                      <td className="mono">
                        {r.entity_table || "—"} <br />
                        <span className="muted small">
                          {(r.entity_id || "").slice(0, 8).toUpperCase()}
                        </span>
                      </td>
                      <td className="mono small">
                        {JSON.stringify(r.payload || {}).slice(0, 120)}
                        {JSON.stringify(r.payload || {}).length > 120
                          ? "…"
                          : ""}
                      </td>
                      <td className="center">
                        {status === "PENDING" ? (
                          <div className="btn-row">
                            <button
                              className="icon-btn ok"
                              onClick={() => decide(r.id, "APPROVE")}
                              disabled={busy || !isAdmin}
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              className="icon-btn danger"
                              onClick={() => decide(r.id, "REJECT")}
                              disabled={busy || !isAdmin}
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <span
                            className={`chip ${
                              status === "APPROVED" ? "ok" : "danger"
                            }`}
                          >
                            {status}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
